import { useState, type MouseEvent } from "react";
import { useDontAskAgain } from "../../utils/useDontAskAgain";
import { Button, Tooltip, Typography } from "antd";
import AskVivoDeleteConfirmModal from "./AskVivoDeleteConfirmModal";
import DeleteIcon from "../../assets/icons/delete.svg?react";
import {
  AV_CONTROL_BUTTON_SIZE,
  AV_HISTORY_DELETE_ICON,
  AV_HISTORY_ROW_ACTIVE_BG,
  AV_HISTORY_ROW_TEXT,
} from "./askVivo.constants";
import { deriveChatTitle } from "./askVivo.utils";
import type { ChatMessage } from "../Chat/chat";

const { Text } = Typography;

interface HistoryRowProps {
  item: ChatMessage;
  isActive: boolean;
  onSelect: (sessionId: string) => void;
  onDelete: (sessionId: string) => void;
  rowTestId?: string;
  textTestId?: string;
}

export default function AskVivoHistoryRow({
  item,
  isActive,
  onSelect,
  onDelete,
  rowTestId,
  textTestId,
}: HistoryRowProps) {
  const [confirming, setConfirming] = useState(false);
  const { dontAskAgain, setDontAskAgain } = useDontAskAgain(
    "askVivo.skipDeleteConfirm",
  );
  const sessionId = item.session_id;
  const chatTitle = deriveChatTitle(item);

  if (!sessionId) return null;

  const handleDeleteClick = (e: MouseEvent) => {
    e.stopPropagation();
    if (dontAskAgain) {
      onDelete(sessionId);
    } else {
      setConfirming(true);
    }
  };

  return (
    <>
      <div
        role="button"
        tabIndex={0}
        data-testid={rowTestId}
        onClick={() => onSelect(sessionId)}
        onKeyDown={(event) => {
          if (event.key === "Enter" || event.key === " ") {
            event.preventDefault();
            onSelect(sessionId);
          }
        }}
        className="history-item-wrapper group relative w-full flex items-center justify-between rounded-sm px-0.75 text-left border-none p-0 cursor-pointer bg-transparent"
        style={{
          backgroundColor: isActive ? AV_HISTORY_ROW_ACTIVE_BG : undefined,
        }}
      >
        <Text
          ellipsis={{ tooltip: { title: chatTitle, mouseEnterDelay: 2 } }}
          className="p-0.5 mt-1 mb-1 flex-1 min-w-0 text-sm"
          data-testid={textTestId}
          style={{ color: AV_HISTORY_ROW_TEXT }}
        >
          {chatTitle}
        </Text>
        <div className="hidden group-hover:flex items-center ml-2 shrink-0">
          <Tooltip title="Delete chat">
            <Button
              type="text"
              size="small"
              aria-label="Delete chat"
              data-testid="floating-history-item-delete"
              onClick={handleDeleteClick}
              style={{
                width: AV_CONTROL_BUTTON_SIZE,
                height: AV_CONTROL_BUTTON_SIZE,
                minWidth: AV_CONTROL_BUTTON_SIZE,
                padding: 0,
                border: "none",
              }}
            >
              <DeleteIcon
                style={{
                  width: 20,
                  height: 20,
                  color: AV_HISTORY_DELETE_ICON,
                }}
              />
            </Button>
          </Tooltip>
        </div>
      </div>
      <AskVivoDeleteConfirmModal
        open={confirming}
        onCancel={() => setConfirming(false)}
        onConfirm={() => onDelete(sessionId)}
        saveDontAskAgain={setDontAskAgain}
      />
    </>
  );
}
